import React, { useState, useEffect, useRef } from "react";
import InvoiceSection from "./invoiceSection";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import docaid from "../../../assets/doc-aid.png";

// ============================================
// LOCALSTORAGE HELPERS
// ============================================

const getSubscriptionHistory = () => {
  try {
    return JSON.parse(localStorage.getItem("subscriptionHistory")) || [];
  } catch {
    return [];
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const InvoiceSendOnboarding = ({ reset, formData, doctorId }) => {
  const [amount, setAmount] = useState(0);
  const [gst, setGst] = useState(0);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const invoiceRef = useRef(null);

  // Load the latest invoice from subscription history
  useEffect(() => {
    const history = getSubscriptionHistory();
    if (history.length > 0) {
      const latest = history[history.length - 1];
      setInvoiceDetails(latest);
    }
    setLoading(false);
  }, []);

  // Calculate Amount and GST when invoiceDetails are available
  useEffect(() => {
    if (invoiceDetails?.amount) {
      const calculatedAmount = (invoiceDetails.amount * 100) / 118;
      setAmount(calculatedAmount.toFixed(2));
      setGst(((invoiceDetails.amount - calculatedAmount) / 2).toFixed(2));
    }
  }, [invoiceDetails]);

  // Generate and Download PDF
  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) return;

    // Hide elements with "hidden-in-pdf" class
    const elementsToHide = document.querySelectorAll(".hidden-in-pdf");
    elementsToHide.forEach((el) => (el.style.display = "none"));

    try {
      // Capture the invoice as an image
      const canvas = await html2canvas(invoiceRef.current, { scale: 1.5 });
      const imgData = canvas.toDataURL("image/jpeg", 0.7);

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);

      // Download PDF
      pdf.save("invoice.pdf");
      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF.");
    } finally {
      // Restore visibility of hidden elements
      elementsToHide.forEach((el) => (el.style.display = ""));
    }
  };

  // If loading, show a placeholder
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg">Loading invoice...</div>
      </div>
    );
  }

  // If no invoice, show a message
  if (!invoiceDetails) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full">
          <div className="text-center">
            <p className="text-lg font-semibold">No Invoice Found</p>
            <p className="text-gray-600 mt-2">Your subscription invoice will appear here.</p>
            <button
              onClick={reset}
              className="mt-4 bg-teal-500 text-white px-6 py-2 rounded-lg hover:bg-teal-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const paymentId = invoiceDetails?.paymentId || "N/A";
  const orderId = invoiceDetails?.orderId || "N/A";
  const paymentDate = invoiceDetails?.paymentDate || "N/A";
  const totalAmount = invoiceDetails?.amount?.toFixed(2) || "0.00";

  return (
    <div
      id="login-popup"
      tabIndex="-1"
      className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center"
    >
      <div className="relative p-4 w-full max-w-4xl h-full md:h-[99%]">
        <div className="relative bg-white rounded-lg shadow w-full h-[99%] my-2 max-h-screen overflow-y-auto">
          <button
            type="button"
            className="absolute top-3 right-2.5 text-gray-400 hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5"
            onClick={reset}
            aria-label="Close popup"
          >
            <svg
              aria-hidden="true"
              className="w-5 h-5"
              fill="#c6c7c7"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          <div className="p-5" ref={invoiceRef}>
            <footer className="text-gray-600 body-font">
              <div className="container px-5 py-8 mx-auto flex items-center sm:flex-row flex-col">
                <div className="lg:w-3/4 mb-0">
                  <img
                    id="docAidImg"
                    alt="Company Logo"
                    className="w-[120px] xl:w-[170px] lg:w-auto mb-0"
                    src={docaid}
                  />
                </div>
              </div>
            </footer>

            <section className="text-gray-600 body-font">
              <div className="container mx-auto">
                <div className="flex flex-col sm:flex-row items-start mx-auto">
                  <div className="flex-grow text-md font-medium title-font text-gray-900">
                    <div className="px-3 py-2">
                      <div className="text-gray-900 font-bold">From</div>
                      <div className="text-lg font-bold">M/s Reshita</div>
                      <div>Flat no. 33 Mangaldeep Apartment</div>
                      <div>Patliputra Colony, Patna, 800013</div>
                      <div className="font-bold">GST No. - 10KGMPK1521A1Z2</div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 border-0 py-2 px-8 rounded text-lg mt-10 sm:mt-0">
                    <div className="text-gray-900 font-bold">Bill to</div>
                    <div>{`${formData?.firstName || ""} ${formData?.lastName || ""}`}</div>
                    <div>{formData?.email || "N/A"}</div>
                    <div>{formData?.contact || "N/A"}</div>
                  </div>
                </div>
              </div>
            </section>

            <div className="px-5">
              <div className="pt-6">
                <span className="font-bold">Payment Id: </span>{paymentId}
              </div>
              <div className="py-1">
                <span className="font-bold">Order Id: </span>{orderId}
              </div>
              <div className="py-1">
                <span className="font-bold">Invoice No: </span>RE/24-25/{invoiceDetails?.id + 2}
              </div>
              <div>
                <span className="font-bold">Payment Date: </span>{paymentDate}
              </div>

              <div className="text-xl font-bold text-red-400 mt-4">
                Your Account Summary
              </div>
              <div className="text-md text-black font-semibold mt-2">
                Subscription Charges
              </div>
              <div className="bg-gray-100 my-5">
                <InvoiceSection label="Previous Balance" value="0.00" />
                <InvoiceSection label="Amount" value={amount} />
                <InvoiceSection label="9% SGST" value={gst} />
                <InvoiceSection label="9% CGST" value={gst} />
                <InvoiceSection label="Total Amount" value={totalAmount} />
                <InvoiceSection
                  label="Adjustable Amount"
                  value={invoiceDetails?.adjustableAmount || 0}
                />
                <InvoiceSection
                  label="Payable Amount"
                  value={
                    invoiceDetails?.adjustableAmount === 0
                      ? totalAmount
                      : (totalAmount - (invoiceDetails?.adjustableAmount || 0)).toFixed(2)
                  }
                />
              </div>
            </div>

            <div className="flex items-center p-1 md:p-5 mb-2 border-gray-200 rounded-b hidden-in-pdf">
              <button
                type="button"
                onClick={handleDownloadPDF}
                className="text-white bg-orange-700 hover:bg-orange-800 focus:ring-4 focus:outline-none focus:ring-orange-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceSendOnboarding;